import { omit } from "lodash";
import { toast } from "react-toastify";
import {
  AdminLeadControllerGetAllQueryParams,
  AdminLeadControllerImportFromCsvRequestBody,
  useAdminLeadControllerGetAllCSV,
  useAdminLeadControllerGetCsvTemplate,
  useAdminLeadControllerImportFromCsv,
} from "service";
import {
  AdminImportLeadsCsvResDto,
  AdminImportLeadsCsvRowErrorDto,
} from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "@/query-client";
import { localFileTimestamp } from "@/utils/date-time";
import { triggerBlobDownload } from "@/utils/download-file";

const csvHeaders = { "Content-Type": "text/csv" } as const;

// How many skipped rows to spell out in the import summary toast.
const MAX_SKIPPED_ROWS_SHOWN = 3;

const isCsvFile = (file: File) =>
  file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

const formatSkippedRows = (skipped: AdminImportLeadsCsvRowErrorDto[]) => {
  const shown = skipped
    .slice(0, MAX_SKIPPED_ROWS_SHOWN)
    .map(({ row, errors }) => `Row ${row}: ${errors.join(", ")}`);
  const remaining = skipped.length - shown.length;
  if (remaining > 0) shown.push(`and ${remaining} more`);
  return shown.join("; ");
};

const notifyImportResult = ({
  createdCount,
  skipped,
}: AdminImportLeadsCsvResDto) => {
  if (createdCount > 0 || skipped.length === 0) {
    toast.success(
      `${createdCount} ${createdCount === 1 ? "lead" : "leads"} imported`,
    );
  }
  if (skipped.length > 0) {
    toast.warning(
      `${skipped.length} ${skipped.length === 1 ? "row" : "rows"} skipped. ${formatSkippedRows(skipped)}`,
    );
  }
};

// Header "Update Leads" actions: CSV template download, CSV import, and
// export of the lead list. Request failures are toasted by the global
// React Query error handlers.
const useLeadsCsv = (queryParams: AdminLeadControllerGetAllQueryParams) => {
  const { refetch: fetchTemplate, isFetching: isTemplateLoading } =
    useAdminLeadControllerGetCsvTemplate(
      { headers: csvHeaders },
      { enabled: false },
    );

  // Every lead matching the current search/sort, not just the visible page.
  const { refetch: fetchLeadList, isFetching: isLeadListLoading } =
    useAdminLeadControllerGetAllCSV(
      {
        queryParams: omit(queryParams, ["page", "limit"]),
        headers: csvHeaders,
      },
      { enabled: false },
    );

  const { mutate: importCsv, isLoading: isImporting } =
    useAdminLeadControllerImportFromCsv({
      onSuccess: (response) => {
        if (response?.data) notifyImportResult(response.data);
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/leads",
            operationId: "adminLeadControllerGetAll",
            variables: {},
          }),
        );
      },
    });

  // The fetcher returns a Blob for non-JSON (CSV) responses.
  const downloadTemplate = async () => {
    const result = await fetchTemplate();
    if (result.isSuccess) {
      triggerBlobDownload(
        result.data as unknown as Blob,
        `lead-template-${localFileTimestamp()}.csv`,
      );
    }
  };

  const downloadLeadList = async () => {
    const result = await fetchLeadList();
    if (result.isSuccess) {
      triggerBlobDownload(
        result.data as unknown as Blob,
        `leads-${localFileTimestamp()}.csv`,
      );
    }
  };

  const importFile = (file: File) => {
    if (!isCsvFile(file)) {
      toast.error("Please select a valid CSV file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    importCsv({
      // The generated body type can't express FormData.
      body: formData as unknown as AdminLeadControllerImportFromCsvRequestBody,
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  return {
    downloadTemplate,
    downloadLeadList,
    importFile,
    isTemplateLoading,
    isLeadListLoading,
    isImporting,
  };
};

export default useLeadsCsv;
